<script setup>
import { computed } from 'vue'
import ActionMenuButton from '../src/ActionMenuButtonMenu.vue'

// Props kept lightweight for JS projects.
// Expecting the same shape used across your action-menu buttons:
//  - editor: TipTap Editor instance
//  - extension: the font-family extension instance (to read .options.fontFamilies)
//  - t: i18n translate fn
const props = defineProps({
  editor: { type: Object, required: true },
  extension: { type: Object, required: true },
  t: { type: Function, required: true },
})

// Fallback when your constants are not present
const DEFAULT_FONT_FAMILY_VALUE = 'inherit'

const items = computed(() => {
  const fontFamilies = (props.extension?.options?.fontFamilies) || []

  return fontFamilies.map(k => {
    const title = typeof k?.title === 'string' ? props.t(k.title) : ''
    const value = k?.value ?? DEFAULT_FONT_FAMILY_VALUE
    const isDivider = !!k?.divider
    const isDefault = !!k?.default

    return {
      title,
      isActive: () => {
        const hasTextStyle = !!props.editor?.schema?.marks?.textStyle
        if (!hasTextStyle) return false
        const attrs = props.editor.getAttributes?.('textStyle') || {}
        const current = attrs.fontFamily
        const isDefaultChoice = value === DEFAULT_FONT_FAMILY_VALUE
        const notSet = current === undefined
        if (isDefaultChoice && notSet) return true
        return props.editor.isActive?.('textStyle', { fontFamily: value }) || false
      },
      action: () => {
        const chain = props.editor.chain().focus()
        if (value === DEFAULT_FONT_FAMILY_VALUE) {
          if (chain.unsetFontFamily) chain.unsetFontFamily().run()
        } else {
          if (chain.setFontFamily) chain.setFontFamily(value).run()
        }
      },
      style: { fontFamily: value },
      divider: isDivider,
      default: isDefault,
    }
  })
})
</script>

<template>
  <ActionMenuButton
    :editor="props.editor"
    :items="items"
    :max-height="280"
    :disabled="!(
      props.editor?.schema?.marks?.textStyle &&
      props.editor?.can?.().setFontFamily ? props.editor.can().setFontFamily('Arial') : false
    )"
    :tooltip="props.t?.('editor.fontFamily.tooltip')"
    icon="fontFamily"
  />
</template>
